import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { eur, fmtData, numRef } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Receipt } from "lucide-react";
import { toast } from "sonner";

export default function NotasCredito() {
  const [list, setList] = useState([]);
  const [verTodas, setVerTodas] = useState(false);
  const [dlg, setDlg] = useState(null);
  const [numero, setNumero] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/notas-credito", { params: { incluir_creditadas: verTodas } }).then((r) => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [verTodas]);

  const registar = async () => {
    try {
      await api.post(`/atendimentos/${dlg.atendimento_id}/linhas/${dlg.linha_id}/nota-credito`, { numero });
      toast.success("Nota de crédito registada.");
      setDlg(null); setNumero(""); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const pendentes = list.filter((x) => x.estado === "devolvido");
  return (
    <div className="max-w-6xl mx-auto" data-testid="notas-credito-page">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Receipt size={22} className="text-orange-500" /> Notas de crédito</h1>
          <p className="text-sm text-slate-500">Linhas devolvidas pelos clientes à espera de nota de crédito.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-600" data-testid="nc-total-pendente">{pendentes.length} por creditar · {eur(pendentes.reduce((s, x) => s + (x.total || 0), 0))}</span>
          <Button size="sm" variant="outline" onClick={() => setVerTodas(!verTodas)} data-testid="nc-toggle-todas">{verTodas ? "Só por creditar" : "Ver também creditadas"}</Button>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
            <tr>
              <th className="text-left px-3 py-2">Pedido</th><th className="text-left px-3 py-2">Cliente</th>
              <th className="text-left px-3 py-2">Artigo</th><th className="text-left px-3 py-2">Motivo</th>
              <th className="text-left px-3 py-2">Devolvido</th><th className="text-right px-3 py-2">Valor</th><th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {list.map((x) => (
              <tr key={x.linha_id} className="border-t border-slate-100" data-testid={`nc-row-${x.linha_id}`}>
                <td className="px-3 py-2"><button className="font-semibold text-sky-700 hover:underline" onClick={() => navigate(`/pedido/${x.atendimento_id}`)}>{numRef(x.numero, x.codigo)}</button></td>
                <td className="px-3 py-2">{x.cliente || "—"}</td>
                <td className="px-3 py-2 uppercase">{x.descricao}{x.ref_marca && <span className="block font-mono text-[10px] text-slate-400">{x.ref_marca} · x{x.qtd || 1}</span>}</td>
                <td className="px-3 py-2 text-slate-600">{x.devolucao?.motivo}{x.devolucao?.substituir && <span className="block text-[10px] text-sky-600">com peça de substituição</span>}</td>
                <td className="px-3 py-2 whitespace-nowrap text-slate-500">{x.devolucao?.at ? fmtData(x.devolucao.at) : "—"}</td>
                <td className="px-3 py-2 text-right font-semibold">{eur(x.total || 0)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {x.estado === "devolvido" ? (
                    <Button size="sm" className="h-7 text-xs bg-orange-600 hover:bg-orange-700" onClick={() => { setDlg(x); setNumero(""); }} data-testid={`nc-registar-${x.linha_id}`}>Nota de crédito emitida</Button>
                  ) : <span className="text-emerald-700 font-semibold">NC {x.nota_credito?.numero}</span>}
                </td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={7} className="text-center text-slate-400 py-10" data-testid="nc-vazio">Sem devoluções à espera de nota de crédito.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nota de crédito emitida</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">{dlg && `${numRef(dlg.numero, dlg.codigo)} · ${dlg.descricao} · ${eur(dlg.total || 0)}`}</p>
          <Input placeholder="Nº da nota de crédito (ex.: NC 2026/15)" value={numero} onChange={(e) => setNumero(e.target.value)} data-testid="nc-numero-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Voltar</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" disabled={!numero.trim()} onClick={registar} data-testid="nc-confirmar">Registar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
